import React from 'react';
import SheetShell from '../common/SheetShell/SheetShell';
import { PrimaryButton, InvertedButton } from '../common/Buttons';
import AttentionBadges from './AttentionBadges';
import CouponBanner from './CouponBanner';
import sunburstImg from '../../images/smart-summary/sunburst.svg';
import discountIconImg from '../../images/smart-summary/discount-icon.svg';
import crossIcon from '../../images/smart-summary/cross.svg';
import './SmartSummaryBottomSheet.css';

/**
 * Smart Summary bottom sheet (home screen).
 *
 * Purely presentational: it renders whatever `data` (login-info's
 * smartSummaryData) contains and delegates all behaviour to callbacks.
 * Capping / mark-shown / navigation live in the container.
 *
 * Variant is driven by data, not hardcoded copy:
 *   attentionItems non-empty  → red status + risk badges
 *   attentionItems empty      → green status, no badges
 * All text (status, card, discount, coupon) comes from the backend.
 */
export default function SmartSummaryBottomSheet({
  visible,
  data = null,
  title = 'Your smart summary for lab test is ready!',
  onClose,
  onViewReport,
  onViewSupplements,
}) {
  if (!data) return null;

  const {
    attentionItemCardStatusTxt,
    attentionItemCardTxt,
    attentionItemCardDiscountTxt,
    discountImg,
    discountCouponTxt,
    attentionItems,
  } = data;

  const hasAttention =
    Array.isArray(attentionItems) && attentionItems.length > 0;

  return (
    <SheetShell
      visible={visible}
      onClose={onClose}
      closeOnOverlayClick={false}
      className="ssbs"
      contentLabel="Smart summary"
    >
      <div className="ssbs__bg" aria-hidden="true" />
      <img className="ssbs__sunburst" src={sunburstImg} alt="" aria-hidden="true" />

      <div className="ssbs__content">
        <div className="ssbs__top">
          <button
            type="button"
            className="ssbs__close"
            onClick={onClose}
            aria-label="Close"
          >
            <img src={crossIcon} alt="" />
          </button>
          <h2 className="ssbs__title">{title}</h2>
        </div>

        <div className="ssbs__card">
          {attentionItemCardStatusTxt && (
            <p
              className={`ssbs__status ${
                hasAttention ? 'ssbs__status--attention' : 'ssbs__status--good'
              }`}
            >
              {attentionItemCardStatusTxt}
            </p>
          )}

          {hasAttention && <AttentionBadges items={attentionItems} />}

          {attentionItemCardTxt && (
            <p className="ssbs__card-sub">{attentionItemCardTxt}</p>
          )}

          {attentionItemCardDiscountTxt && (
            <div className="ssbs__discount">
              <img
                className="ssbs__discount-icon"
                src={discountIconImg}
                alt=""
                aria-hidden="true"
              />
              <span className="ssbs__discount-text">
                {attentionItemCardDiscountTxt}
              </span>
            </div>
          )}
        </div>

        <CouponBanner couponTxt={discountCouponTxt} discountImg={discountImg} />

        <div className="ssbs__cta">
          <div className="ssbs__cta-item ssbs__cta-item--ghost">
            <InvertedButton color="#714fff" onTap={onViewReport}>
              <span className="ssbs__btn-label">View Report</span>
            </InvertedButton>
          </div>
          <div className="ssbs__cta-item">
            <PrimaryButton onTap={onViewSupplements}>
              <span className="ssbs__btn-label">View Supplements</span>
            </PrimaryButton>
          </div>
        </div>
      </div>
    </SheetShell>
  );
}
